/**
 * article.js — Therabot Article Page
 *
 * Custom Elements:
 *   <pi-progress>  — reading progress bar tied to scroll position
 *
 * Additional modules (plain functions, no custom elements needed):
 *   initTocHighlight()   — highlights active ToC link on scroll
 *   initScrollReveal()   — IntersectionObserver entrance animations
 *   initSmoothScroll()   — polished anchor scroll with nav offset
 */

'use strict';

/* ================================================================
   UTILITY
   ================================================================ */

/**
 * Read a numeric CSS variable value (strips 'px').
 * @param {string} prop
 * @param {Element} [el=document.documentElement]
 * @returns {number}
 */
function getCSSVarPx(prop, el = document.documentElement) {
  const raw = getComputedStyle(el).getPropertyValue(prop).trim();
  return parseFloat(raw) || 0;
}

/**
 * Throttle a function to fire at most once per animation frame.
 * @param {Function} fn
 * @returns {Function}
 */
function rafThrottle(fn) {
  let queued = false;
  return function (...args) {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      fn.apply(this, args);
      queued = false;
    });
  };
}

/**
 * Emit a CustomEvent from an element.
 * @param {Element} el
 * @param {string} name
 * @param {*} [detail]
 */
function emit(el, name, detail = null) {
  el.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
}


/* ================================================================
   CUSTOM ELEMENT: <pi-progress>
   Tracks scroll position through the article body and updates
   the progress bar width (0%–100%).

   Observed attributes:
     target  — CSS selector of the content element to measure
               defaults to '#article-content' or <main>
   ================================================================ */

class PiProgress extends HTMLElement {
  /** @type {HTMLElement|null} */
  #bar          = null;
  /** @type {HTMLElement|null} */
  #target       = null;
  /** @type {Function} */
  #onScroll;
  /** @type {ResizeObserver|null} */
  #resizeObs    = null;

  static get observedAttributes() {
    return ['target'];
  }

  connectedCallback() {
    this.#bar = this.querySelector('.pi-progress__bar');

    // Allow a brief paint before attaching — ensures layout is stable
    requestAnimationFrame(() => {
      this.#resolveTarget();
      this.#bindScroll();
      this.#update(); // initial paint
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    this.#resizeObs?.disconnect();
  }

  attributeChangedCallback(name) {
    if (name === 'target') {
      this.#resolveTarget();
      this.#update();
    }
  }

  #resolveTarget() {
    const selector = this.getAttribute('target') || '#article-content';
    this.#target =
      document.querySelector(selector) ||
      document.querySelector('main') ||
      document.body;
  }

  #bindScroll() {
    this.#onScroll = rafThrottle(() => this.#update());
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.#resizeObs = new ResizeObserver(rafThrottle(() => this.#update()));
      this.#resizeObs.observe(document.body);
    }
  }

  #update() {
    if (!this.#bar || !this.#target) return;

    const rect       = this.#target.getBoundingClientRect();
    const scrollTop  = window.scrollY;
    const targetTop  = rect.top + scrollTop;
    const targetH    = this.#target.offsetHeight;

    // Progress: 0 when target top is at viewport top, 1 when bottom exits viewport
    const viewH    = window.innerHeight;
    const progress = Math.max(0, Math.min(1,
      (scrollTop - targetTop + viewH * 0.15) / (targetH - viewH * 0.7)
    ));

    const pct = (progress * 100).toFixed(2);
    this.#bar.style.width = pct + '%';
    this.setAttribute('aria-valuenow', Math.round(progress * 100));

    emit(this, 'pi-progress:update', { progress, pct: parseFloat(pct) });
  }

  /** Public API */
  get progress() {
    return parseFloat(this.getAttribute('aria-valuenow') || '0') / 100;
  }
}

customElements.define('pi-progress', PiProgress);


/* ================================================================
   TOC ACTIVE HIGHLIGHT
   Watches all article sections via IntersectionObserver and
   marks the corresponding ToC link with [data-active].
   ================================================================ */

function initTocHighlight() {
  const tocLinks = Array.from(document.querySelectorAll('.pi-toc__link'));
  if (!tocLinks.length) return;

  const sectionIds = tocLinks
    .map(a => a.getAttribute('href')?.slice(1))
    .filter(Boolean);

  const sections = sectionIds
    .map(id => document.getElementById(id))
    .filter(Boolean);

  if (!sections.length) return;

  /** @type {Set<string>} currently visible section ids */
  const visible = new Set();

  const navH     = getCSSVarPx('--pi-nav-height');
  const progressH = getCSSVarPx('--pi-progress-height');
  const offset    = navH + progressH + 32; // px below sticky nav

  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          visible.add(entry.target.id);
        } else {
          visible.delete(entry.target.id);
        }
      });

      // Highlight the first currently visible section (document order)
      let activeId = null;
      for (const id of sectionIds) {
        if (visible.has(id)) { activeId = id; break; }
      }

      tocLinks.forEach(link => {
        const href = link.getAttribute('href')?.slice(1);
        if (href === activeId) {
          link.setAttribute('data-active', '');
        } else {
          link.removeAttribute('data-active');
        }
      });
    },
    {
      rootMargin: `-${offset}px 0px -60% 0px`,
      threshold: 0,
    }
  );

  sections.forEach(s => obs.observe(s));
}


/* ================================================================
   SMOOTH SCROLL — respect sticky nav offset
   ================================================================ */

function initSmoothScroll() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const id = link.getAttribute('href').slice(1);
    const target = document.getElementById(id);
    if (!target) return;

    e.preventDefault();

    const navH      = getCSSVarPx('--pi-nav-height');
    const progressH = getCSSVarPx('--pi-progress-height');
    const gap       = 24; // px extra breathing room
    const offset    = navH + progressH + gap;

    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: 'smooth' });

    // Update URL without triggering jump
    history.pushState(null, '', '#' + id);
  });
}


/* ================================================================
   SCROLL REVEAL
   Animate article components on entry.
   ================================================================ */

function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const SELECTORS = [
    '.pi-tldr',
    '.pi-section',
    '.pi-pullquote',
    '.pi-callout',
    '.pi-steps',
    '.pi-step',
    '.pi-cta-module',
    '.pi-safety',
    '.pi-related',
    '.pi-related-card',
  ].join(', ');

  // Inject animation styles
  const style = document.createElement('style');
  style.textContent = `
    @media (prefers-reduced-motion: no-preference) {
      .pi-tldr,
      .pi-section,
      .pi-pullquote,
      .pi-callout,
      .pi-steps,
      .pi-step,
      .pi-cta-module,
      .pi-safety,
      .pi-related,
      .pi-related-card {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 480ms cubic-bezier(0.16, 1, 0.3, 1),
          transform 480ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      .pi-tldr[data-visible],
      .pi-section[data-visible],
      .pi-pullquote[data-visible],
      .pi-callout[data-visible],
      .pi-steps[data-visible],
      .pi-step[data-visible],
      .pi-cta-module[data-visible],
      .pi-safety[data-visible],
      .pi-related[data-visible],
      .pi-related-card[data-visible] {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);

  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        // Stagger items within the same parent
        const siblings = Array.from(entry.target.parentElement?.children ?? []);
        const idx      = siblings.indexOf(entry.target);
        const stagger  = Math.min(idx * 70, 280);

        entry.target.style.transitionDelay = stagger + 'ms';
        entry.target.setAttribute('data-visible', '');
        obs.unobserve(entry.target);
      });
    },
    {
      threshold: 0.06,
      rootMargin: '0px 0px -48px 0px',
    }
  );

  document.querySelectorAll(SELECTORS).forEach(el => obs.observe(el));
}


/* ================================================================
   ESTIMATED READ TIME UPDATER
   Dynamically counts words in the article body and updates
   the read-time element if present.
   ================================================================ */

function initReadTime() {
  const body    = document.getElementById('article-content');
  const display = document.querySelector('.pi-article-hero__read-time');
  if (!body || !display) return;

  const text      = body.innerText || body.textContent || '';
  const wordCount = text.trim().split(/\s+/).length;
  const WPM       = 220; // average adult reading speed
  const minutes   = Math.ceil(wordCount / WPM);
  const label     = minutes === 1 ? '1 min read' : `${minutes} min read`;

  // Replace text node after the SVG icon
  const textNode = Array.from(display.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
  if (textNode) {
    textNode.textContent = ' ' + label;
  } else {
    display.insertAdjacentText('beforeend', ' ' + label);
  }
}


/* ================================================================
   BOOT
   ================================================================ */

document.addEventListener('DOMContentLoaded', () => {
  initTocHighlight();
  initSmoothScroll();
  initScrollReveal();
  initReadTime();
});
